import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Settings as SettingsIcon, Building2, Layers, Palette, Tag, RefreshCw, ShieldCheck, Trash2, ImagePlus, Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

function DataCard({ title, icon: Icon, items, render, addForm }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="font-bold text-slate-700 flex items-center gap-2 mb-3"><Icon className="h-4 w-4 text-indigo-500" />{title}</p>
      <div className="space-y-2">{items.map(render)}</div>
      {items.length === 0 && <p className="text-xs text-slate-400 italic py-2">Belum ada data.</p>}
      {addForm}
    </div>
  );
}

function InlineAdd({ fields, onAdd, busy }) {
  const [vals, setVals] = useState({});
  const submit = () => {
    const missing = fields.filter((f) => f.required && !vals[f.key]);
    if (missing.length) return toast.error(`${missing[0].label} wajib diisi`);
    onAdd(vals, () => setVals({}));
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2 items-end">
      {fields.map((f) => (
        <div key={f.key} className={f.w || "w-24"}>
          <Label className="text-[10px] text-slate-400">{f.label}</Label>
          <Input type={f.type || "text"} value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} placeholder={f.placeholder} className="h-8 text-xs" data-testid={`add-${f.key}`} />
        </div>
      ))}
      <Button size="sm" onClick={submit} disabled={busy} className="h-8 bg-indigo-600 hover:bg-indigo-700" data-testid="master-add-btn"><Plus className="h-3.5 w-3.5 mr-1" />Tambah</Button>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [levels, setLevels] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const isSuperAdmin = ["super_admin", "owner"].includes(user?.role);

  const loadMaster = () => {
    api.get("/org/levels").then((r) => setLevels(r.data));
    api.get("/divisions").then((r) => setDivisions(r.data));
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/brands").then((r) => setBrands(r.data));
  };
  useEffect(() => {
    loadMaster();
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const addMaster = async (url, payload, done, label) => {
    setBusy(true);
    try { await api.post(url, payload); toast.success(`${label} ditambahkan`); loadMaster(); if (done) done(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menambah"); }
    setBusy(false);
  };

  const reseed = async () => {
    if (!window.confirm("Reset & isi ulang seluruh data seed Fase 1? Data saat ini akan diganti.")) return;
    setBusy(true);
    try { const r = await api.post("/admin/reseed"); toast.success(`Seed ulang selesai (${r.data.tasks} tugas)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    setBusy(false);
  };

  const wipe = async () => {
    const c = window.prompt("Tindakan ini menghapus SEMUA data (termasuk master data & semua user lain) dan hanya menyisakan akun Anda. Ketik HAPUS untuk konfirmasi.");
    if (c !== "HAPUS") { if (c !== null) toast.error("Konfirmasi tidak cocok"); return; }
    setBusy(true);
    try { const r = await api.post("/admin/wipe-data"); toast.success(r.data.message || "Semua data dihapus"); setTimeout(() => window.location.reload(), 800); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus data"); }
    setBusy(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><SettingsIcon className="h-7 w-7 text-indigo-500" />Pengaturan</h1>
        <p className="text-slate-500 text-sm">Master data & konfigurasi sistem</p>
      </div>

      <Tabs defaultValue="master">
        <TabsList className="mb-6">
          <TabsTrigger value="master">Master Data</TabsTrigger>
          <TabsTrigger value="sistem">Konfigurasi Sistem</TabsTrigger>
          {isSuperAdmin && <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>}
          {isSuperAdmin && <TabsTrigger value="akses" data-testid="tab-akses">Hak Akses Fitur</TabsTrigger>}
        </TabsList>

        <TabsContent value="master">
          <div className="grid md:grid-cols-2 gap-5">
            <DataCard title="Level Jabatan" icon={Layers} items={levels} render={(l) => (
              <div key={l.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                <span className="font-medium text-slate-700">{l.name}</span><span className="text-xs font-mono text-slate-400">Rank {l.rank}</span>
              </div>
            )} addForm={isSuperAdmin && (
              <InlineAdd busy={busy} fields={[{ key: "name", label: "Nama Level", required: true, w: "flex-1" }, { key: "rank", label: "Rank", type: "number", required: true, w: "w-20" }]}
                onAdd={(v, done) => addMaster("/levels", { name: v.name, rank: Number(v.rank) }, done, "Level")} />
            )} />
            <DataCard title="Divisi" icon={Palette} items={divisions} render={(d) => (
              <div key={d.id} className="flex items-center gap-2 text-sm border-b border-slate-100 pb-1.5">
                <span className="h-3 w-3 rounded-full" style={{ background: d.color }} />
                <span className="font-medium text-slate-700">{d.name}</span><span className="text-xs text-slate-400">({d.code})</span>
              </div>
            )} addForm={isSuperAdmin && (
              <InlineAdd busy={busy} fields={[{ key: "name", label: "Nama Divisi", required: true, w: "flex-1" }, { key: "code", label: "Kode", required: true, w: "w-20" }, { key: "color", label: "Warna", type: "color", w: "w-16" }]}
                onAdd={(v, done) => addMaster("/divisions", { name: v.name, code: v.code, color: v.color || "#6366F1" }, done, "Divisi")} />
            )} />
            <DataCard title="Cabang" icon={Building2} items={branches} render={(b) => (
              <div key={b.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                <span className="font-medium text-slate-700">{b.name}</span><span className="text-xs text-slate-400">{b.city}</span>
              </div>
            )} addForm={isSuperAdmin && (
              <InlineAdd busy={busy} fields={[{ key: "name", label: "Nama Cabang", required: true, w: "flex-1" }, { key: "code", label: "Kode", required: true, w: "w-20" }, { key: "city", label: "Kota", w: "w-24" }]}
                onAdd={(v, done) => addMaster("/branches", { name: v.name, code: v.code, city: v.city || null }, done, "Cabang")} />
            )} />
            <DataCard title="Brand" icon={Tag} items={brands} render={(b) => (
              <div key={b.id} className="text-sm border-b border-slate-100 pb-1.5 font-medium text-slate-700">{b.name}</div>
            )} />
          </div>
        </TabsContent>

        <TabsContent value="sistem">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-3 max-w-2xl">
            <Row label="SLA Kritis" value={`${settings.sla_hours?.kritis || 24} jam`} />
            <Row label="SLA Tinggi" value={`${settings.sla_hours?.tinggi || 72} jam`} />
            <Row label="SLA Normal" value={`${settings.sla_hours?.normal || 168} jam`} />
            <Row label="SLA Rendah" value={`${settings.sla_hours?.rendah || 336} jam`} />
            <Row label="Span of Control (maks bawahan langsung)" value={settings.span_of_control || 12} />
            <Row label="Kelipatan Slider Progres" value={`${settings.slider_step || 5}%`} />
            <Row label="Batas Pengisian Laporan Keterlambatan" value={`${settings.late_report_deadline_hours || 24} jam`} />
            <Row label="Ambang Masalah Berulang" value={`${settings.recurring_problem_threshold || 3}x / 30 hari`} />
          </div>
          {isSuperAdmin && (
            <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-5 max-w-2xl">
              <p className="font-bold text-rose-800 flex items-center gap-2"><Trash2 className="h-4 w-4" />Zona Berbahaya</p>
              <p className="text-sm text-rose-700 mb-3">Hapus SEMUA data contoh dan mulai dari nol. Menghapus seluruh tugas, target, rapat, struktur, master data & user lain — hanya menyisakan akun Anda. Tindakan ini tidak dapat dibatalkan.</p>
              <Button onClick={wipe} disabled={busy} variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100" data-testid="wipe-data-btn"><Trash2 className="h-4 w-4 mr-1.5" />{busy ? "Memproses..." : "Hapus Semua Data (Mulai Kosong)"}</Button>
            </div>
          )}
        </TabsContent>

        {isSuperAdmin && (
          <TabsContent value="branding">
            <BrandingSettings />
          </TabsContent>
        )}

        {isSuperAdmin && (
          <TabsContent value="akses">
            <FeatureMatrix />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function FeatureMatrix() {
  const [features, setFeatures] = useState([]);
  const [roles, setRoles] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [busy, setBusy] = useState(false);
  const LOCKED = ["super_admin", "owner"];
  const ROLE_LABEL = {
    super_admin: "Super Admin", owner: "Owner", kepala_divisi: "Kepala Divisi",
    atasan: "Atasan", staff: "Staff", verifikator: "Verifikator", auditor: "Auditor",
  };

  useEffect(() => {
    api.get("/access/matrix").then((r) => {
      setFeatures(r.data.features); setRoles(r.data.roles); setMatrix(r.data.matrix);
    });
  }, []);

  const toggle = (role, key) => {
    if (LOCKED.includes(role)) return;
    setMatrix((m) => {
      const cur = new Set(m[role] || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...m, [role]: Array.from(cur) };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {}; roles.forEach((r) => { if (!LOCKED.includes(r)) payload[r] = matrix[r] || []; });
      const res = await api.put("/access/matrix", { matrix: payload });
      setMatrix(res.data.matrix);
      toast.success("Matriks hak akses disimpan");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="feature-matrix">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <p className="font-bold text-slate-700 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" />Hak Akses Fitur per Peran</p>
        <Button onClick={save} disabled={busy} size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-matrix-btn">{busy ? "Menyimpan…" : "Simpan Perubahan"}</Button>
      </div>
      <p className="text-xs text-slate-400 mb-4">Super Admin & Owner selalu memiliki akses penuh (terkunci). Override per pengguna tersedia di Manajemen Pengguna.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-3 py-2.5 text-xs uppercase text-slate-500 sticky left-0 bg-slate-50">Fitur</th>
              {roles.map((r) => <th key={r} className="px-2 py-2.5 text-xs font-semibold text-slate-600 whitespace-nowrap">{ROLE_LABEL[r] || r}</th>)}
            </tr>
          </thead>
          <tbody>
            {features.map((f) => (
              <tr key={f.key} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium text-slate-700 sticky left-0 bg-white whitespace-nowrap">{f.label}</td>
                {roles.map((r) => {
                  const locked = LOCKED.includes(r);
                  const checked = locked ? true : (matrix[r] || []).includes(f.key);
                  return (
                    <td key={r} className="text-center">
                      <Checkbox checked={checked} disabled={locked} onCheckedChange={() => toggle(r, f.key)} data-testid={`matrix-${r}-${f.key}`} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex items-center justify-between text-sm border-b border-slate-100 pb-2"><span className="text-slate-600">{label}</span><span className="font-semibold text-slate-800">{value}</span></div>;
}

function BrandingSettings() {
  const { branding, reloadBranding } = useBranding();
  const [name, setName] = useState("");
  const [tagline, setTagline] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setName(branding.app_name || ""); setTagline(branding.tagline || ""); setLogo(branding.logo || null); }, [branding.app_name, branding.tagline, branding.logo]);

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Ukuran gambar maksimal 2MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setLogo(ev.target.result);
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/branding", { app_name: name, tagline, logo });
      await reloadBranding();
      toast.success("Branding disimpan");
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan branding"); }
    setBusy(false);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-w-2xl" data-testid="branding-settings">
      <p className="font-bold text-slate-700 flex items-center gap-2 mb-4"><ImagePlus className="h-4 w-4 text-indigo-500" />Branding Aplikasi</p>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-2xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            {logo ? <img src={logo} alt="logo" className="h-16 w-16 object-contain" data-testid="branding-logo-preview" /> : <ImagePlus className="h-6 w-6 text-slate-300" />}
          </div>
          <div className="flex-1">
            <Label className="text-xs">Logo (tampil di tab browser, sidebar & login)</Label>
            <Input type="file" accept="image/*" onChange={onFile} data-testid="branding-logo-input" className="mt-1" />
            {logo && <button onClick={() => setLogo(null)} className="text-xs text-rose-500 hover:underline mt-1" data-testid="branding-logo-remove">Hapus logo</button>}
          </div>
        </div>
        <div className="space-y-1.5"><Label>Nama Aplikasi</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="branding-name-input" placeholder="ZOA Mission Control" /></div>
        <div className="space-y-1.5"><Label>Tagline (sidebar)</Label><Input value={tagline} onChange={(e) => setTagline(e.target.value)} data-testid="branding-tagline-input" placeholder="Mission Control" /></div>
        <Button onClick={save} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-branding-btn">{busy ? "Menyimpan…" : "Simpan Branding"}</Button>
      </div>
    </div>
  );
}
